import styled from 'styled-components';
import assistantClient from '@requests/telnyx/assistants.requests';
import toolClient from '@requests/telnyx/tools.requests';

import { Chip, IconButton, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { AIAssistant, AIAssistantCreate, AIAssistantUpdate, AssistantBase } from '@requests/telnyx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssistantModel } from '@common/Autocomplete/AssistantModel';
import { useTheme } from '../../../../../styles';
import { Button, LoadingButton } from '@common/Button';
import { RequestError } from '@requests/common';
import { useSnackbar } from 'notistack';
import { ContentCopyOutlined, DownloadOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Label } from '@common/Label';
import { copyToClipboard } from '@common/index';
import { AssistantContainer, AssistantContainerSection } from '../Assistants';
import { assistantActions, getAssistantChanges } from '@store/slices/assistant';
import { useDispatch, useSelector } from 'react-redux';
import { showTelnyxFeatures, State } from '@store/index';
import { useNavigate, useParams } from 'react-router-dom';
import { useSecrets } from '@common/hooks/useSecrets';
import { isPaidModel } from '@common/Autocomplete/Base';
import { Secrets } from '@common/Autocomplete/Secrets';
import { AITool } from './AssistantTools/tool.types';
import { formatAssistantName } from '@components/Flow/Nodes/Standard/Assistant/AssistantNodeChips';

export const Footer = styled.div<{ margin?: number }>`
  display: flex;
  position: sticky;
  bottom: 0;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.background.standard};
  border-radius: 8px;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.line.light};
  z-index: 1000;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);

  ${({ margin }) => margin && `margin: ${margin}px`};

  p {
    font-size: 14px;
    color: ${({ theme }) => theme.text.standard};
  }
`;

export type FieldError = {
  id: string;
  error: string;
} | null;

interface Props {
  assistant: AIAssistant;
  isAssistantsLoading: boolean;
}

export const Assistant: React.FC<Props> = ({ assistant }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { secrets, loading, refetch } = useSecrets();

  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));
  const createAssistant = useSelector((state: State) => state.assistant.createAssistant);
  const assistantChanges = useSelector((state: State) => getAssistantChanges(state, assistant.id));
  const assistantName = assistantChanges !== undefined ? assistantChanges.name : assistant.name;
  const assistantDescription = assistantChanges !== undefined ? assistantChanges.description : assistant.description;
  const assistantModel = assistantChanges !== undefined ? assistantChanges.model : assistant.model;
  const assistantInstructions = assistantChanges !== undefined ? assistantChanges.instructions : assistant.instructions;
  const assistantAuthSecret =
    assistantChanges !== undefined ? assistantChanges.llm_api_key_ref : assistant.llm_api_key_ref;

  const [fieldError, setFieldError] = useState<FieldError>(null);

  const updateAssistant: AIAssistant = {
    ...assistant,
    name: assistantName,
    description: assistantDescription,
    model: assistantModel,
    instructions: assistantInstructions,
    llm_api_key_ref: assistantAuthSecret,
  };

  const isModelAuthenticated = isPaidModel(assistantModel);

  const unsavedChanges = JSON.stringify(assistant) !== JSON.stringify(updateAssistant);

  const { data: flowTools = [], isLoading: flowToolsLoading } = useQuery<
    AITool[],
    RequestError,
    AITool[],
    [string, string | undefined]
  >({
    queryKey: ['ai_assistants_tools', assistant.id],
    queryFn: toolClient.list,
    retry: false,
    enabled: Boolean(assistant.id),
  });

  const { mutateAsync: create, isPending: creatingAssistant } = useMutation<
    AIAssistant,
    RequestError,
    AIAssistantCreate
  >({
    mutationFn: assistantClient.create,
    onSuccess: (newAssistant) => {
      queryClient.setQueryData(['ai_assistants'], (oldData: AIAssistant[] | undefined) => {
        const currentData = oldData || [];
        return currentData.concat(newAssistant);
      });

      enqueueSnackbar('Assistant created successfully!', { variant: 'success', autoHideDuration: 3000 });

      clearChanges();
      dispatch(assistantActions.createNewAssistant(null));
      navigate(`/${service_id}/assistants/${newAssistant.id}`);
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to create assistant!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const { mutateAsync: update, isPending: updatingAssistant } = useMutation<void, RequestError, AIAssistantUpdate>({
    mutationFn: assistantClient.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_assistants'] });
      enqueueSnackbar('Assistant updated successfully!', { variant: 'success', autoHideDuration: 3000 });
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to update assistant!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const saveChanges = async () => {
    if (assistantName.length <= 3 || assistantName.length > 64) {
      return setFieldError({
        id: 'assistantName',
        error: 'Name must be more than 3 characters and less than 64 characters.',
      });
    }

    if (!assistantModel) {
      return setFieldError({ id: 'assistantModel', error: 'A large language model must be specified.' });
    }

    if (!assistantDescription) {
      return setFieldError({
        id: 'assistantDescription',
        error: 'Each assistant should have a description of what it is designed to do.',
      });
    }

    if (!assistantInstructions) {
      return setFieldError({
        id: 'assistantInstructions',
        error: 'A system message is required for directing the assistant during execution.',
      });
    }

    if (isModelAuthenticated && !assistantAuthSecret) {
      return setFieldError({
        id: 'assistantAuthSecret',
        error: `"${assistantModel}" is a paid model that requires an authentication token to generate requests.`,
      });
    }

    try {
      if (createAssistant) {
        const newAssistant: AssistantBase = {
          name: assistantName,
          description: assistantDescription,
          model: assistantModel,
          instructions: assistantInstructions,
          llm_api_key_ref: assistantAuthSecret,
        };

        await create({ assistant: newAssistant });
      } else {
        await update({ assistantId: assistant.id, assistant: updateAssistant });
      }
    } catch {}
  };

  const updateField = (field: string, value: string | null) => {
    setFieldError(null);
    updateAssistantField(field, value);
  };

  const updateAssistantField = (field: string, value: string | null) => {
    dispatch(
      assistantActions.updateAssistantChanges({
        id: assistant.id,
        name: assistantName,
        description: assistantDescription,
        model: assistantModel,
        instructions: assistantInstructions,
        llm_api_key_ref: assistantAuthSecret,
        [field]: value,
      })
    );
  };

  const clearChanges = () => {
    dispatch(assistantActions.clearAssistantChanges(assistant.id));
    setFieldError(null);
  };

  const downloadAssistant = () => {
    try {
      const assistantJSON: AIAssistant = {
        ...assistant,
        tools: flowTools,
      };

      const blob = new Blob([JSON.stringify(assistantJSON, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formatAssistantName(assistant.name)}-${new Date().getTime()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      enqueueSnackbar('Failed to download workflow as JSON!', { variant: 'error', autoHideDuration: 5000 });
    }
  };

  return (
    <AssistantContainer>
      <AssistantContainerSection>
        {!createAssistant && (
          <Stack display='flex' direction='row' justifyContent='space-between' alignItems='center'>
            <Stack spacing={0.5} direction='row' alignItems='center'>
              <Chip label={assistant.id} size='small' />
              <IconButton size='small' onClick={() => copyToClipboard(assistant.id)}>
                <ContentCopyOutlined sx={{ fontSize: '12px' }} />
              </IconButton>
            </Stack>
            {isTelnyxUser && (
              <Button startIcon={<DownloadOutlined />} small disabled={flowToolsLoading} onClick={downloadAssistant}>
                Export
              </Button>
            )}
          </Stack>
        )}
        <Stack spacing={1}>
          <Label title='Name' tooltip='The name of the assistant.' />
          <TextField
            fullWidth
            size='small'
            value={assistantName}
            error={fieldError?.id === 'assistantName'}
            helperText={fieldError?.id === 'assistantName' ? fieldError.error : undefined}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder='Assistant Name'
          />
        </Stack>
        <Stack spacing={1}>
          <Label title='Description' tooltip='A description of what the assistant is designed to do.' />
          <TextField
            fullWidth
            size='small'
            value={assistantDescription}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder='Assistant Description'
            error={fieldError?.id === 'assistantDescription'}
            helperText={fieldError?.id === 'assistantDescription' ? fieldError.error : undefined}
            multiline
            maxRows={8}
            minRows={1}
          />
        </Stack>
        <Stack direction='row' spacing={1} width='100%'>
          <Stack spacing={1} flex={1}>
            <Label title='Model' tooltip='The large language model this assistant will use to generate responses.' />
            <AssistantModel
              portal
              value={assistantModel}
              error={fieldError?.id === 'assistantModel' ? fieldError.error : undefined}
              onChange={(e, newValue) => updateField('model', newValue.value)}
              placeholder='Model'
            />
          </Stack>
          {isModelAuthenticated && (
            <Stack spacing={1} flex={1}>
              <Label
                title='API Key'
                tooltip='The selected AI model requires an authentication token to generate requests. Please provide a secret that contains the API key for the model you are using.'
              />
              <Secrets
                refetch={refetch}
                value={assistantAuthSecret || ''}
                loading={loading}
                secrets={secrets.filter((secret) => secret.type === 'bearer')}
                portal
                onChange={(_, item) => {
                  if (!item?.value) updateField('llm_api_key_ref', null);
                  else updateField('llm_api_key_ref', item.value.identifier);
                }}
                error={fieldError?.id === 'assistantAuthSecret' ? fieldError.error : undefined}
                placeholder='Model Authentication'
              />
            </Stack>
          )}
        </Stack>
        <Stack spacing={1}>
          <Label
            title='Instructions'
            tooltip='The instructions provided to the large language model determining how it behaves.'
          />
          <TextField
            fullWidth
            size='small'
            value={assistantInstructions}
            onChange={(e) => updateField('instructions', e.target.value)}
            placeholder='System Message'
            error={fieldError?.id === 'assistantInstructions'}
            helperText={fieldError?.id === 'assistantInstructions' ? fieldError.error : undefined}
            multiline
            minRows={4}
            maxRows={12}
          />
        </Stack>

        {(unsavedChanges || createAssistant) && (
          <Footer>
            <Stack spacing={1} direction='row' alignItems='center'>
              <WarningAmberRounded sx={{ color: theme.text.standard }} />
              <p>
                {createAssistant
                  ? 'Assistant not saved yet. Tools will be available after creating the assistant.'
                  : 'You have unsaved changes.'}
              </p>
            </Stack>
            <Stack spacing={1} direction='row'>
              {!createAssistant && <Button onClick={clearChanges}>Clear</Button>}
              {createAssistant && (
                <Button onClick={() => dispatch(assistantActions.createNewAssistant(null))}>Cancel</Button>
              )}
              <LoadingButton
                customColor='black'
                minWidth={createAssistant ? '147px' : '123px'}
                minHeight='32px'
                onClick={saveChanges}
                loading={updatingAssistant || creatingAssistant}
              >
                {createAssistant ? 'Create Assistant' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Footer>
        )}
      </AssistantContainerSection>
    </AssistantContainer>
  );
};
